// PartnerDetail.cshtml (QOR staff). Tabs: Overview (what it is, its status, and its Partner API clients — PartnerApiPanel,
// for programs whose processor is open to partners), Profile (2026-10-22, PartnerProfilePanel: the legacy entity
// profile — card issuing, money limits, cardholder features and the inactivity fee (for reference) in one table, one save), Funding
// (PartnerFundingPanel: its prefunded fund and settings) and Points. The Fees tab went into Profile (2026-10-22): the fee is
// a row of the profile; an old ?tab=fees link opens Profile. Branding
// (co-branding: logo, colors, own domain) is the last tab (2026-10-22). Kiosks (PartnerKiosksPanel) moved here from a page
// of their own (2026-10-22): a kiosk goes with its partner. Since 2026-10-23 the tab shows only while the partner's
// kiosks are on (the switch is on Overview; a location follows its partner), and Points is called Earn.
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { adminApi, type BrandProfile, type ProgramSummary } from '../api';
import { useStaff } from '../components/RequireStaff';
import { usePageTitle } from '../components/ConsoleLayout';
import { PageHeading, ResultText } from '../components/ui';
import { PartnerApiPanel } from './PartnerApiPanel';
import { PartnerFundingPanel } from './PartnerFundingPanel';
import { PartnerKiosksPanel } from './PartnerKiosksPanel';
import { PartnerPointsPanel } from './PartnerPointsPanel';
import { PartnerProfilePanel } from './PartnerProfilePanel';

/** The processors whose programs may have Partner API clients (the API says the same: PARTNER_PROVIDERS). */
const PARTNER_PROCESSORS = new Set(['Central Payments']);

const TABS = [
  ['overview', 'Overview'],
  ['profile', 'Profile'],
  ['kiosks', 'Kiosks'],
  ['funding', 'Funding'],
  ['points', 'Earn'],
  ['branding', 'Branding'],
] as const;
type Tab = (typeof TABS)[number][0];

export function PartnerDetailPage() {
  const { id = '' } = useParams();
  const client = useQueryClient();
  const data = useQuery({
    queryKey: ['partner', id],
    queryFn: async () => {
      const partner = (await adminApi.programs()).find((p) => p.id === id) ?? null;
      if (!partner) return { partner, features: [], branding: null, limits: null };
      const [features, branding, limits] = await Promise.all([
        adminApi.programFeatures(id),
        adminApi.branding(id),
        adminApi.limits(id),
      ]);
      return { partner, features, branding, limits };
    },
  });
  const partner = data.data?.partner ?? null;
  usePageTitle(partner?.displayName ?? 'Partner');
  const { user } = useStaff();
  const isAdmin = user.role === 'SystemAdmin' || user.role === 'ProgramAdmin';
  const [outcome, setOutcome] = useState<{
    message: string | null;
    ok: boolean;
  }>({ message: null, ok: false });

  async function after(ok: boolean, message: string) {
    setOutcome({ ok, message });
    await client.invalidateQueries({ queryKey: ['partner', id] });
  }

  const [params, setParams] = useSearchParams();
  const asked = params.get('tab');
  const tabs = TABS.filter(([key]) => key !== 'kiosks' || partner?.kiosksEnabled === true);
  const tab: Tab = asked === 'fees' ? 'profile' : (tabs.find(([key]) => key === asked)?.[0] ?? 'overview');

  if (data.isPending) return null;
  return (
    <>
      <PageHeading title={partner?.displayName ?? 'Partner'} trail={[['Partners', '/partners']]} />
      <ResultText {...outcome} />
      {partner && (
        <div className="qc-segmented" role="tablist" aria-label="Partner sections" style={{ marginBottom: 16 }}>
          {tabs.map(([key, label]) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-pressed={tab === key}
              aria-selected={tab === key}
              onClick={() => setParams(key === 'overview' ? {} : { tab: key }, { replace: true })}
            >
              {label}
            </button>
          ))}
        </div>
      )}
      {partner && tab === 'profile' ? (
        <PartnerProfilePanel
          key={data.dataUpdatedAt}
          partner={partner}
          features={data.data!.features}
          limits={data.data!.limits}
          after={(ok, message) => void after(ok, message)}
        />
      ) : partner && tab === 'kiosks' ? (
        <PartnerKiosksPanel partner={partner} after={(ok, message) => void after(ok, message)} />
      ) : partner && tab === 'funding' ? (
        <PartnerFundingPanel programId={partner.id} after={(ok, message) => void after(ok, message)} />
      ) : partner && tab === 'points' ? (
        <PartnerPointsPanel programId={partner.id} after={(ok, message) => void after(ok, message)} />
      ) : partner && tab === 'branding' ? (
        <BrandingPanel
          key={data.dataUpdatedAt}
          partner={partner}
          branding={data.data!.branding}
          after={(ok, message) => void after(ok, message)}
        />
      ) : (
        <>
          {!partner ? (
            <p className="qc-empty">Partner not found.</p>
          ) : (
            <PartnerSummary partner={partner} after={(ok, message) => void after(ok, message)} />
          )}
          {partner && partner.parentProgramId === null && PARTNER_PROCESSORS.has(partner.provider) && isAdmin && (
            <PartnerApiPanel programId={partner.id} after={(ok, message) => void after(ok, message)} />
          )}
        </>
      )}
    </>
  );
}

/** Overview: what the partner is, and switching it on or off. Everything it is set to is on Profile. */
function PartnerSummary({
  partner,
  after,
}: {
  partner: ProgramSummary;
  after: (ok: boolean, message: string) => void;
}) {
  const { user } = useStaff();
  const qorAdmin = user.role === 'SystemAdmin' && user.internal;
  const [busy, setBusy] = useState(false);

  async function toggleKiosks() {
    setBusy(true);
    const r = await adminApi.setKiosks(partner.id, !partner.kiosksEnabled);
    setBusy(false);
    after(
      r.success,
      r.success
        ? partner.kiosksEnabled
          ? 'Kiosks switched off.'
          : 'Kiosks switched on: the Kiosks tab is open.'
        : (r.error ?? "Couldn't change the kiosks."),
    );
  }

  async function toggleActive() {
    setBusy(true);
    const r = await adminApi.setProgramActive(partner.id, !partner.isActive);
    setBusy(false);
    after(r.success, r.success ? 'Status updated.' : "Couldn't update status.");
  }

  return (
    <div className="qc-panel">
      <div className="qc-panel-title">
        <h3>Partner</h3>
        <span>KYC, money limits, features and fees are on Profile; the logo and colors on Branding.</span>
      </div>
      <dl className="qc-detail-dl">
        <div>
          <dt>Entity ID</dt>
          <dd>{partner.code}</dd>
        </div>
        <div>
          <dt>Type</dt>
          <dd>{partner.parentProgramId ? `Location of ${partner.parentProgramName ?? 'a partner'}` : 'Partner'}</dd>
        </div>
        <div>
          <dt>Processor</dt>
          <dd>{partner.provider}</dd>
        </div>
        {partner.instantIssueAvailable && (
          <div>
            <dt>KYC</dt>
            <dd>{partner.instantIssue ? 'Not required (staff issue cards)' : 'Required (cardholders apply)'}</dd>
          </div>
        )}
        <div>
          <dt>Status</dt>
          <dd>
            <span className={`qc-badge ${partner.isActive ? 'qc-badge-success' : 'qc-badge-muted'}`}>
              {partner.isActive ? 'Active' : 'Inactive'}
            </span>
          </dd>
        </div>
      </dl>
      <div style={{ marginTop: 20 }}>
        <p
          style={{
            color: 'var(--qor-muted)',
            fontSize: '.8rem',
            fontWeight: 700,
            margin: '0 0 10px',
          }}
        >
          Kiosks
        </p>
        {partner.parentProgramId ? (
          <p className="qc-field-hint">
            {partner.kiosksEnabled ? 'On' : 'Off'}: a location follows {partner.parentProgramName ?? 'its partner'}.
          </p>
        ) : (
          <label className="qc-switch">
            <input
              type="checkbox"
              role="switch"
              name="KiosksEnabled"
              checked={partner.kiosksEnabled}
              disabled={busy || !qorAdmin}
              onChange={() => void toggleKiosks()}
            />
            <span className="qc-switch-track" aria-hidden="true"></span>
            <span>
              {partner.kiosksEnabled ? 'On' : 'Off'}
              <small className="qc-field-hint">
                {' '}
                {partner.kiosksEnabled
                  ? '— its kiosks work and the Kiosks tab shows them.'
                  : '— its kiosks are refused and the Kiosks tab is hidden.'}
                {!qorAdmin && " QOR's system administrators switch this."}
              </small>
            </span>
          </label>
        )}
      </div>
      <div style={{ marginTop: 20 }}>
        <p
          style={{
            color: 'var(--qor-muted)',
            fontSize: '.8rem',
            fontWeight: 700,
            margin: '0 0 10px',
          }}
        >
          Account access
        </p>
        <button type="button" className="qc-button-ghost" disabled={busy} onClick={() => void toggleActive()}>
          {partner.isActive ? 'Deactivate this partner' : 'Activate this partner'}
        </button>
      </div>
    </div>
  );
}

/** Branding tab: the logo, colors and own domain this partner's cardholders see. */
function BrandingPanel({
  partner,
  branding,
  after,
}: {
  partner: ProgramSummary;
  branding: BrandProfile | null;
  after: (ok: boolean, message: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [brand, setBrand] = useState({
    displayName: branding?.displayName ?? partner.displayName,
    domainHost: branding?.domainHost ?? '',
    logoUrl: branding?.logoUrl ?? '',
    primaryColor: branding?.primaryColor ?? '#12c8c0',
    secondaryColor: branding?.secondaryColor ?? '#040a19',
  });
  async function saveBranding(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const file = (e.currentTarget.elements.namedItem('LogoFile') as HTMLInputElement | null)?.files?.[0] ?? null;
    setBusy(true);
    let logoUrl: string | null = brand.logoUrl === '' ? null : brand.logoUrl;
    if (file instanceof File && file.size > 0) {
      const upload = await adminApi.uploadLogo(partner.id, file);
      if (!upload.success) {
        setBusy(false);
        return after(false, upload.error ?? "Couldn't upload the logo.");
      }
      logoUrl = upload.logoUrl;
    }
    const r = await adminApi.setBranding(partner.id, {
      domainHost: brand.domainHost === '' ? null : brand.domainHost,
      displayName: brand.displayName,
      logoUrl,
      primaryColor: brand.primaryColor,
      secondaryColor: brand.secondaryColor,
    });
    setBusy(false);
    after(r.success, r.success ? 'Branding saved.' : (r.error ?? "Couldn't save branding."));
  }

  async function removeBranding() {
    setBusy(true);
    const ok = await adminApi.removeBranding(partner.id);
    setBusy(false);
    after(ok, ok ? 'Custom branding removed -- back to the default QOR look.' : "Couldn't remove branding.");
  }

  return (
    <div className="qc-panel">
      <div className="qc-panel-title">
        <h3>Branding (co-branding)</h3>
        <span>
          Logo and colors this partner&apos;s cardholders see. Applies after sign-in today; add a dedicated domain below
          and it applies from the sign-in page too.
        </span>
      </div>
      <form onSubmit={(e) => void saveBranding(e)} className="qc-account-field">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
          <label>
            <span>Display name</span>
            <input
              type="text"
              name="BrandDisplayName"
              required
              value={brand.displayName}
              onChange={(e) => setBrand({ ...brand, displayName: e.target.value })}
            />
          </label>
          <label>
            <span>Dedicated domain (optional)</span>
            <input
              type="text"
              name="DomainHost"
              placeholder="e.g. client1.qorteams.com"
              value={brand.domainHost}
              onChange={(e) => setBrand({ ...brand, domainHost: e.target.value })}
            />
          </label>
        </div>
        <label>
          <span>Logo</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <input type="file" name="LogoFile" accept="image/png,image/jpeg,image/webp,image/gif" style={{ flex: 1 }} />
            {branding?.logoUrl && (
              <img
                src={branding.logoUrl}
                alt="Logo preview"
                style={{
                  height: 32,
                  width: 'auto',
                  maxWidth: 120,
                  objectFit: 'contain',
                }}
              />
            )}
          </div>
        </label>
        <p
          style={{
            color: 'var(--qor-muted)',
            fontSize: '.78rem',
            margin: '-8px 0 0',
          }}
        >
          PNG, JPEG, WEBP or GIF, up to 2MB. Uploading a new one replaces the current logo.
        </p>
        <label>
          <span>Or paste an external logo URL instead</span>
          <input
            type="text"
            name="LogoUrl"
            placeholder="https://..."
            value={brand.logoUrl}
            onChange={(e) => setBrand({ ...brand, logoUrl: e.target.value })}
          />
        </label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
          <label>
            <span>Primary color</span>
            <input
              type="color"
              name="PrimaryColor"
              style={{ height: 40, padding: 2 }}
              value={brand.primaryColor}
              onChange={(e) => setBrand({ ...brand, primaryColor: e.target.value })}
            />
          </label>
          <label>
            <span>Secondary color</span>
            <input
              type="color"
              name="SecondaryColor"
              style={{ height: 40, padding: 2 }}
              value={brand.secondaryColor}
              onChange={(e) => setBrand({ ...brand, secondaryColor: e.target.value })}
            />
          </label>
        </div>
        <div
          style={{
            display: 'flex',
            gap: 12,
            alignItems: 'center',
            marginTop: 6,
          }}
        >
          <button type="submit" className="qor-button qor-button-primary" disabled={busy}>
            Save branding
          </button>
          {branding && <span style={{ color: 'var(--qor-muted)', fontSize: '.8rem' }}>Custom branding active</span>}
        </div>
      </form>
      {branding && (
        <div style={{ marginTop: 14 }}>
          <button type="button" className="qc-button-ghost" disabled={busy} onClick={() => void removeBranding()}>
            Remove branding / reset to default QOR look
          </button>
        </div>
      )}
    </div>
  );
}
